import { createSelector, createSlice } from '@reduxjs/toolkit';
import { current } from 'immer';
import { isEmpty, isNumber } from 'lodash';
import { configService } from 'services/config.service';
import type { TAppThunk, TRootState } from 'slices/store';
import type { TObjectTypes, TObjectTypesDetail } from 'types/state.interface';
import { getAllData } from 'utils/fetchAllData';
import {
  TEMP_ID,
  type TTypeLocal,
  buildTypeTree,
  convertDataTypesForm2BodyCreate,
  convertDataTypesForm2BodyUpdate,
  createTypesLocal,
  isNewObjTypeId,
} from 'utils/objectTypes';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  stateHasFetchedAllObjectsSuccess,
} from 'utils/sliceHelpers';
import type { TType } from '@timeedit/types';
import type { TAppDispatch } from '..';
import api from '../services/api.service';

export const initialState: TObjectTypes = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
  totalResults: 0,
  firstDrawer: null,
  secondDrawer: null,
  externalOwners: [],
  typeTree: {},
};

// Slice
const slice = createSlice({
  name: 'objectTypes',
  initialState,
  reducers: {
    // =============== get list of object types ===============
    fetchObjectMapping: (state) => {
      isLoadingRequest(state);
    },
    fetchObjectTypesSuccess: (state, { payload }) => {
      stateHasFetchedAllObjectsSuccess(payload, state, (i: any) => i);
      state.typeTree = buildTypeTree(state.results);
      finishedLoadingSuccess(state);
    },
    fetchObjectMappingFailure: (state) => {
      finishedLoadingFailure(state);
    },
    // ========= get detail of object type ===============
    fetchObjectTypesDetailSuccess: (state, { payload }) => {
      const { isFirstDrawer, data, advanced } = payload;
      state.map[data.id] = data;
      if (isFirstDrawer) {
        if (advanced && state.firstDrawer) {
          state.firstDrawer.advancedSettings = advanced;
        }
      } else {
        if (advanced && state.secondDrawer) {
          state.secondDrawer.advancedSettings = advanced;
        }
      }
      finishedLoadingSuccess(state);
    },
    fetchObjectTypesDetail: (state, { payload }) => {
      const { isFirstDrawer, id, record } = payload;

      if (isFirstDrawer) {
        state.firstDrawer = record || state.map[id];
      } else {
        state.secondDrawer = record || state.map[id];
      }

      if (!record) {
        removeLocalObjectType(state);
      }
    },
    fetchObjectTypesDetailFailure: (state) => {
      finishedLoadingSuccess(state);
    },
    // =============== update object types local ===============
    updateDrawerObjectType: (state, { payload }) => {
      const { isFirstDrawer, data } = payload;

      if (isFirstDrawer) {
        state.firstDrawer = data;
      } else {
        state.secondDrawer = data;
      }

      if (!data) {
        removeLocalObjectType(state);
      }
    },
    // ============= request update object types ===============
    updateObjectTypesDetailSuccess(state, { payload }) {
      const { data, isFirstDrawer } = payload;
      if (isFirstDrawer && state?.firstDrawer) {
        state.firstDrawer = null;
      }
      if (!isFirstDrawer && state?.secondDrawer) {
        state.secondDrawer = null;
      }
      state.map[data.id] = data;
      const dataCurrent = current(state.results);
      const index = dataCurrent.findIndex((i: any) => i.id === data.id);
      if (index !== -1) {
        state.results[index] = data;
      }
      finishedLoadingSuccess(state);
    },
    // ========= delete object type ===============

    deleteObjectType: (state) => {
      isLoadingRequest(state);
    },
    deleteObjectTypeSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
    deleteObjectTypeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    // =============== create object type ===============

    addNewObjectTypeLocal: (state, { payload }) => {
      const newObjType = createTypesLocal(payload);

      if (state.map[newObjType.id]) {
        state.firstDrawer = state.map[newObjType.id];
        return;
      }

      state.map[newObjType.id] = newObjType;

      if (payload) {
        const index = state.results.findIndex((type) => type.id === payload.id);
        const parent = state.results[index];

        if (parent) {
          const newSubTypes = [newObjType.id, ...(parent.subTypes || [])];
          state.results[index].subTypes = newSubTypes;
          state.map[parent.id].subTypes = newSubTypes.slice();
        }
      }

      state.firstDrawer = newObjType;
      state.results = [newObjType, ...state.results];
    },
    requestCreateObjectType(state) {
      isLoadingRequest(state);
    },
    createObjectTypeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteLocalObjectType: (state, { payload }) => {
      state.results = state.results.filter((item) => {
        return !payload.includes(item.id);
      });
      payload.forEach((id: any) => {
        if (state.map[id]) {
          const parentFields = state.map[id]?.parentFields;
          if (parentFields && parentFields.length) {
            const index = state.results.findIndex(
              (x) => x.id === parentFields[parentFields.length - 1]
            );
            if (index !== -1) {
              state.results[index].subTypes = state.results[
                index
              ].subTypes.filter((i: string | number) => i !== id);
            }
          }
          state.results = state.results.filter((x) => x.id !== id);
          state.map[id] = null;
        }
      });
      if (isNewObjTypeId(state?.firstDrawer?.id)) {
        state.firstDrawer = null;
      }
    },
    // ExternalOwners
    fetchExternalOwnersSuccess: (state, { payload }) => {
      if (payload) state.externalOwners = payload;
    },
    fetchExternalOwnersFailure: (state) => {
      finishedLoadingFailure(state);
    },
    // onDiscardForm
    onDiscardForm: (state, { payload }) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = state.map[id];
      } else {
        state.secondDrawer = state.map[id];
      }
    },
  },
});

const assertLocalObjectType = (type?: TType): type is TTypeLocal =>
  type !== undefined && 'parentIds' in type;

function removeLocalObjectType(state: TObjectTypes) {
  const index = state.results.findIndex((type) => type.id === TEMP_ID);

  if (index === -1) {
    return;
  }

  const [localObjectType] = state.results.splice(index, 1);

  if (!assertLocalObjectType(localObjectType)) {
    return;
  }

  delete state.map[TEMP_ID];
  const parentId = localObjectType.parentIds.at(-1);
  const parentIndex = state.results.findIndex((type) => type.id === parentId);
  const parent = state.results[parentIndex];

  if (!parent) {
    return;
  }

  const newSubTypes = parent.subTypes.filter((id) => id !== TEMP_ID);
  parent.subTypes = newSubTypes;
  state.map[parentId].subTypes = newSubTypes.slice();
}

export default slice.reducer;

// Selectors
export const selectObjectTypesLoading = (state: TRootState) =>
  state.objectTypes.loading;
export const selectObjectTypesMap = (state: TRootState) =>
  state.objectTypes.map;
export const selectObjectTypesTree = (state: TRootState) =>
  state.objectTypes.typeTree;
export const selectFirstDrawer = (state: TRootState) =>
  state.objectTypes.firstDrawer;
export const selectSecondDrawer = (state: TRootState) =>
  state.objectTypes.secondDrawer;
export const selectObjectTypesList = (state: TRootState) =>
  state.objectTypes.results;
export const selectExternalOwners = (state: TRootState) =>
  state.objectTypes.externalOwners;

// Actions
export const {
  // get list of object types
  fetchObjectMapping,
  fetchObjectTypesSuccess,
  fetchObjectMappingFailure,

  // detail of object types
  fetchObjectTypesDetailSuccess,
  fetchObjectTypesDetail,
  fetchObjectTypesDetailFailure,
  updateDrawerObjectType,
  updateObjectTypesDetailSuccess,
  // del obj types
  deleteObjectType,
  deleteObjectTypeSuccess,
  deleteObjectTypeFailure,
  deleteLocalObjectType,
  // add new
  addNewObjectTypeLocal,
  requestCreateObjectType,
  createObjectTypeFailure,
  // master data
  fetchExternalOwnersSuccess,
  fetchExternalOwnersFailure,
  //
  onDiscardForm,
} = slice.actions;

export const requestExternalOwners = () => async (dispatch: TAppDispatch) => {
  try {
    const externalOwners = await api.get({
      endpoint: `/types/external-owners`,
    });
    if (externalOwners.results) {
      dispatch(
        fetchExternalOwnersSuccess(
          externalOwners.results.map((i: any) => ({
            value: i.id,
            label: i.name,
          }))
        )
      );
    }
  } catch (e) {
    dispatch(fetchExternalOwnersFailure());
  }
};

export const requestObjectTypes = () => async (dispatch: TAppDispatch) => {
  try {
    dispatch(fetchObjectMapping());
    const result = await getAllData({
      endpoint: `${configService.REACT_APP_BASE_URL}/types/find`,
      successMessage: false,
    });
    dispatch(fetchObjectTypesSuccess(result));
  } catch (e) {
    dispatch(fetchObjectMappingFailure());
  }
};

export const requestMoveObjectType =
  (recordId: number, destinationId: number): TAppThunk =>
  async (dispatch, getState) => {
    try {
      const allState = getState();

      const parentData = allState.objectTypes.map[
        destinationId
      ]?.subTypes?.filter((id) => id !== TEMP_ID);

      await api.patch({
        endpoint: `${configService.REACT_APP_BASE_URL}/types/${destinationId}`,
        data: {
          subTypes: [...(parentData || []), recordId],
        },
      });

      dispatch(requestObjectTypes());
    } catch (e) {
      dispatch(fetchObjectMappingFailure());
    }
  };

export const requestObjectTypesDetail =
  (isFirstDrawer: boolean, record: any): TAppThunk =>
  async (dispatch) => {
    try {
      const { id, isLocalData } = record;
      dispatch(fetchObjectTypesDetail({ isFirstDrawer, id, record }));
      if (isLocalData) {
        dispatch(fetchObjectTypesDetailFailure());
        return;
      }
      const [result, types] = await Promise.all([
        api.get({
          endpoint: `${configService.REACT_APP_BASE_URL}/types/${id}`,
        }),
        api.get({
          endpoint: `/types/${id}/advanced`,
        }),
      ]);

      dispatch(
        updateDrawerObjectType({
          isFirstDrawer,
          data: {
            ...record,
            ...result,
          },
        })
      );
      dispatch(
        fetchObjectTypesDetailSuccess({
          isFirstDrawer,
          data: {
            ...record,
            ...result,
          },
          advanced: types.results,
        })
      );
    } catch (e) {
      console.log(e, 'error');
      dispatch(fetchObjectTypesDetailFailure());
    }
  };

export const requestUpdateObjectType =
  ({
    isFirstDrawer,
    data,
  }: {
    isFirstDrawer: boolean;
    data: TObjectTypesDetail;
  }): TAppThunk =>
  async (dispatch, getState) => {
    try {
      if (data?.id === undefined) {
        throw new Error('No id provided');
      }
      if (data.isLocalData) {
        // create new
        dispatch(requestCreateObjectType());
        const dataBody = convertDataTypesForm2BodyCreate(data);
        const dataNewObj = await api.post({
          endpoint: `${configService.REACT_APP_BASE_URL}/types`,
          data: dataBody,
          successMessage: 'Object type created successfully',
        });

        if (data.parentIds && data.parentIds[0] && dataNewObj.id) {
          const allState = getState();
          const parentId = data.parentIds[data.parentIds.length - 1];

          const parentData = allState.objectTypes.map[
            parentId
          ]?.subTypes?.filter((id) => id !== TEMP_ID);

          await api.patch({
            endpoint: `${configService.REACT_APP_BASE_URL}/types/${parentId}`,
            data: {
              subTypes: [...(parentData || []), dataNewObj.id],
            },
            successMessage: 'Object type was added to parent',
          });
        }

        dispatch(updateDrawerObjectType({ isFirstDrawer: true, data: null }));
        dispatch(requestObjectTypes());
      } else {
        dispatch(fetchObjectMapping());
        const dataBody = convertDataTypesForm2BodyUpdate(data);
        const result = await api.patch({
          endpoint: `${configService.REACT_APP_BASE_URL}/types/${data.id}`,
          data: dataBody,
        });
        dispatch(
          updateObjectTypesDetailSuccess({
            isFirstDrawer,
            data: { ...result, isEdited: false },
          })
        );
      }
    } catch (e) {
      dispatch(createObjectTypeFailure());
      dispatch(fetchObjectTypesDetailFailure());
    }
  };
export const requestDeleteObjectTypes =
  (objectIds: any[]) => async (dispatch: TAppDispatch) => {
    try {
      const listId = objectIds.filter((i: string | number) => isNumber(i));
      const listLocalId = objectIds.filter(isNewObjTypeId);
      if (!isEmpty(listLocalId)) {
        dispatch(deleteLocalObjectType(listLocalId));
      }
      if (!listId || !listId[0]) return 1;
      dispatch(deleteObjectType());
      await Promise.all(
        listId.map((id) =>
          api.delete({
            endpoint: `${configService.REACT_APP_BASE_URL}/types/${id}`,
          })
        )
      );
      dispatch(requestObjectTypes());
      dispatch(deleteObjectTypeSuccess());
      return 1;
    } catch (e) {
      dispatch(deleteObjectTypeFailure());
      return 0;
    }
  };
export const getDrawerData =
  (isFirstDrawer: boolean): TAppThunk<TObjectTypesDetail> =>
  (dispatch, getState) => {
    try {
      const state = getState();
      const { firstDrawer, secondDrawer } = state.objectTypes;
      return isFirstDrawer ? firstDrawer : secondDrawer;
    } catch (e) {
      dispatch(deleteObjectTypeFailure());
      return null;
    }
  };

export const getListIdSelected = createSelector(
  [
    (state: TRootState) => state.objectTypes.firstDrawer,
    (state: TRootState) => state.objectTypes.secondDrawer,
  ],
  (firstDrawer, secondDrawer) => {
    const result: number[] = [];
    if (firstDrawer) result.push(firstDrawer.id);
    if (secondDrawer) result.push(secondDrawer.id);
    return result;
  }
);
